import React, { useEffect } from 'react';

import CRMApi from '../../../Api/CRM/CRMApi';
import { formatDateStringWithTimezone as formatDateStringTz } from '../../../utils/formatters';
import DetachElement from '../../elements/DetachElement';
import Search from '../../elements/Search';
import ViewButton from '../../elements/ViewButton';
import Card from '../../elements/common/Card';
import TableHead from '../../elements/common/TableHead';
import TableLoader from '../../elements/common/TableLoader';
import Paginator from '../../elements/paginator';
import { PERMISSION } from '../../helpers/Permissions';
import { PermissionsWithDates } from '../../helpers/types';
import useAutoPaginator from '../../hooks/useAutoPaginator';
import { ROLES } from '../../services/AdminPolicyService';

type Props =
    | { source?: 'permissions'; relatedId?: never; showDetach?: never; refresh?: boolean }
    | { source: 'admin' | 'role'; relatedId: number; showDetach: boolean; refresh?: boolean };

export default function PermissionsList({ source = 'permissions', relatedId, showDetach = false, refresh }: Props) {
    const url = source === 'permissions' ? 'permission' : `${source}/${relatedId}/permissions`;

    const { items, updateItems, isLoading, auto } = useAutoPaginator<PermissionsWithDates>(
        async (page, params) => (await CRMApi.get(url, { params: { page, ...params } })).data
    );

    useEffect(() => {
        updateItems();
    }, [refresh]);

    function getLinks(permissionId: number) {
        return source === 'permissions' ? (
            <ViewButton url={`/permissions/${permissionId}`} permissions={[PERMISSION.VIEW_PERMISSION]} />
        ) : (
            showDetach && (
                <DetachElement
                    url={`/crea/api/${url}`}
                    params={{ permission: permissionId }}
                    action={updateItems}
                    roles={ROLES.SUPER_ADMINS}
                />
            )
        );
    }

    return (
        <Card title={source === 'permissions' ? 'All permissions' : 'Permissions'}>
            <Search {...auto.search} />
            <TableLoader isLoading={isLoading}>
                <table className="table model-list">
                    <TableHead>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Created</th>
                        <th>Updated</th>
                        <div />
                    </TableHead>
                    <tbody>
                        {items.map((permission) => (
                            <tr key={permission.id}>
                                <td>{permission.id}</td>
                                <td>{permission.name}</td>
                                <td>{formatDateStringTz(permission.pivot?.created_at ?? permission.created_at)}</td>
                                <td>{formatDateStringTz(permission.pivot?.updated_at ?? permission.updated_at)}</td>
                                <td>{getLinks(permission.id)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Paginator {...auto.paginator} />
            </TableLoader>
        </Card>
    );
}
